import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Input,
  Segmented,
  Select,
  Spin,
  Dropdown,
  message,
} from 'antd';
import {
  Template,
  TemplateBlock,
  BlockType,
  BlockAlign,
  BlockSize,
  TEMPLATE_VARIABLES,
  renderWithSample,
} from 'services/NotificationTemplatesService';
import useStyles from './styles';
import TemplatePreview, { BLOCK_LABELS, isImageBlock } from './TemplatePreview';

const { TextArea } = Input;

interface TemplateEditorModalProps {
  classes: ReturnType<typeof useStyles>;
  open: boolean;
  onClose: () => void;

  onSave: (template: Template) => Promise<void>;
  stateName: string;
  template?: Template;

  loading?: boolean;
}

const BRAND_COLORS = [
  '#1B6FE0',
  '#124FB0',
  '#3B82F6',
  '#3FBF3F',
  '#2E9E2E',
  '#5CC94A',
];

const BLOCK_TYPE_OPTIONS: { type: BlockType; label: string }[] = [
  { type: 'header', label: 'Header (banner)' },
  { type: 'text', label: 'Texto' },
  { type: 'button', label: 'Botón' },
  { type: 'image', label: 'Imagen' },
  { type: 'footer', label: 'Footer (banner)' },
];

const WIDTH_OPTIONS = [
  { label: '700 px (Chazki)', value: 700 },
  { label: '600 px', value: 600 },
  { label: '480 px', value: 480 },
];
const emptyTemplate = (stateName: string): Template => ({

  id: '',
  name: stateName,
  subject: '',
  blocks: [
    { id: 'header', type: 'header', content: '', imageUrl: '' },
    { id: 'text', type: 'text', content: '', align: 'left', size: 'M' },
  ],
  brandColor: '#1B6FE0',
  width: 700,
});

const TemplateEditorModal = ({
  classes,
  open,
  onClose,
  onSave,
  stateName,
  template,
  loading = false,
}: TemplateEditorModalProps) => {
  const [draft, setDraft] = useState<Template>(
    () => template ?? emptyTemplate(stateName),
  );
  const [selectedId, setSelectedId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const base = template ?? emptyTemplate(stateName);
      setDraft(base);
      const firstText = base.blocks.find((b) => b.type === 'text');
      setSelectedId(firstText?.id ?? base.blocks[0]?.id ?? '');
    }
  }, [open, template, stateName]);

  const selected = draft.blocks.find((b) => b.id === selectedId);

  const updateBlock = (id: string, patch: Partial<TemplateBlock>) => {
    setDraft((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    }));
  };

  const insertVariable = (variable: string) => {
    if (!selected || isImageBlock(selected.type)) return;
    updateBlock(selected.id, {
      content: `${selected.content}${selected.content ? ' ' : ''}${variable}`,
    });
  };

  const newBlock = (type: BlockType): TemplateBlock => {
    const id = `${type}-${Date.now()}`;
    switch (type) {
      case 'button':
        return {
          id,
          type,
          content: 'Seguir mi envío',
          url: '{link_tracking}/{seguimiento_b64}',
        };
      case 'header':
      case 'footer':
      case 'image':
        return { id, type, content: '', imageUrl: '' };
      default:
        return { id, type: 'text', content: '', align: 'left', size: 'M' };
    }
  };

  const addBlock = (type: BlockType) => {
    const block = newBlock(type);
    setDraft((prev) => ({ ...prev, blocks: [...prev.blocks, block] }));
    setSelectedId(block.id);
  };

  const addImageVar = (variable: string, idPrefix: string) => {
    const block: TemplateBlock = {
      id: `${idPrefix}-${Date.now()}`,
      type: 'image',
      content: '',
      imageUrl: variable,
    };
    setDraft((prev) => ({ ...prev, blocks: [...prev.blocks, block] }));
    setSelectedId(block.id);
  };

  const addQR = () => {
    const block: TemplateBlock = {
      id: `qr-${Date.now()}`,
      type: 'qr',
      content: '{codigo_paquer}',
      align: 'center',
    };
    setDraft((prev) => ({ ...prev, blocks: [...prev.blocks, block] }));
    setSelectedId(block.id);
  };

  const removeBlock = (id: string) => {
    setDraft((prev) => ({
      ...prev,
      blocks: prev.blocks.filter((b) => b.id !== id),
    }));
    setSelectedId((cur) => (cur === id ? '' : cur));
  };

  const moveBlock = (fromId: string | null, toId: string) => {
    if (!fromId || fromId === toId) return;
    setDraft((prev) => {
      const blocks = [...prev.blocks];
      const from = blocks.findIndex((b) => b.id === fromId);
      const to = blocks.findIndex((b) => b.id === toId);
      if (from === -1 || to === -1) return prev;
      const [moved] = blocks.splice(from, 1);
      blocks.splice(to, 0, moved);
      return { ...prev, blocks };
    });
  };

  const handleSave = async () => {

    if (!draft.subject?.trim()) {
      message.warning('Poné un asunto para el mail antes de guardar.');
      return;
    }

    setSaving(true);
    try {
      await onSave(draft);
      onClose();
    } catch {

    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Editar plantilla — Chazki · ${stateName}`}
      open={open}
      onCancel={onClose}
      width={1040}
      styles={{ body: { padding: 0 } }}
      footer={[
        <Button key="discard" onClick={onClose} disabled={saving}>
          Descartar
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={loading}
        >
          Guardar como plantilla nueva
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div className={classes.editor}>
          <div className={classes.blocks}>
            <div className={classes.fldLbl} style={{ marginTop: 0 }}>
              Asunto del mail
            </div>
            <Input
              value={draft.subject ?? ''}
              placeholder="Tu pedido {numero_seguimiento} está en camino"
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, subject: e.target.value }))
              }
            />
            <div className={classes.fldLbl}>Bloques de la plantilla</div>
            {draft.blocks.map((block) => (
              <div
                key={block.id}
                className={`${classes.blk}${
                  block.id === selectedId ? ` ${classes.blkSel}` : ''
                }`}
                onClick={() => setSelectedId(block.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  moveBlock(dragId, block.id);
                  setDragId(null);
                }}
                role="button"
                tabIndex={0}
              >
                <div className={classes.blkHead}>
                  <span
                    draggable
                    onDragStart={(e) => {
                      e.stopPropagation();
                      setDragId(block.id);
                    }}
                    onDragEnd={() => setDragId(null)}
                    title="Arrastrar para reordenar"
                    style={{ color: '#8c8c8c', cursor: 'grab' }}
                  >
                    ⠿
                  </span>
                  <span className={classes.blkType}>
                    {BLOCK_LABELS[block.type]}
                  </span>
                  <span className={classes.blkVal}>
                    {isImageBlock(block.type)
                      ? block.imageUrl
                        ? '(imagen)'
                        : '(sin imagen)'
                      : block.content}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    title="Eliminar bloque"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBlock(block.id);
                    }}
                    style={{
                      marginLeft: 'auto',
                      color: '#bfbfbf',
                      cursor: 'pointer',
                      fontSize: 13,
                      padding: '0 2px',
                    }}
                  >
                    ✕
                  </span>
                </div>
                {block.id === selectedId && (
                  <div
                    className={classes.blkEdit}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isImageBlock(block.type) ? (
                      <>
                        <div
                          style={{
                            fontSize: 10.5,
                            color: '#8c8c8c',
                            marginBottom: 6,
                          }}
                        >
                          URL de imagen (banner)
                        </div>
                        <Input
                          value={block.imageUrl ?? ''}
                          placeholder="https://…/banner.png"
                          onChange={(e) =>
                            updateBlock(block.id, { imageUrl: e.target.value })
                          }
                        />
                        <div
                          style={{
                            fontSize: 10.5,
                            color: '#8c8c8c',
                            marginTop: 6,
                          }}
                        >
                          Pega la URL de la imagen del banner.
                        </div>
                      </>
                    ) : (
                      <>
                        <TextArea
                          value={block.content}
                          onChange={(e) =>
                            updateBlock(block.id, { content: e.target.value })
                          }
                          autoSize={{ minRows: 2 }}
                        />
                        {block.type === 'button' && (
                          <>
                            <div
                              style={{
                                fontSize: 10.5,
                                color: '#8c8c8c',
                                marginTop: 6,
                              }}
                            >
                              Destino del botón (URL)
                            </div>
                            <Input
                              value={block.url ?? ''}
                              placeholder="{link_tracking}/{seguimiento_b64}"
                              onChange={(e) =>
                                updateBlock(block.id, { url: e.target.value })
                              }
                            />
                          </>
                        )}
                        {block.type === 'text' && (
                          <>
                            <div
                              style={{
                                fontSize: 10.5,
                                color: '#8c8c8c',
                                marginTop: 6,
                              }}
                            >
                              Insertar variable:
                            </div>
                            <div className={classes.vchips}>
                              {TEMPLATE_VARIABLES.map((v) => (
                                <span
                                  key={v}
                                  className={classes.vchip}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => insertVariable(v)}
                                >
                                  {v}
                                </span>
                              ))}
                            </div>
                            <div
                              style={{ display: 'flex', gap: 6, marginTop: 10 }}
                            >
                              <Segmented
                                size="small"
                                value={block.align ?? 'left'}
                                onChange={(val) =>
                                  updateBlock(block.id, {
                                    align: val as BlockAlign,
                                  })
                                }
                                options={[
                                  { label: 'Izq.', value: 'left' },
                                  { label: 'Centro', value: 'center' },
                                  { label: 'Der.', value: 'right' },
                                ]}
                              />
                              <Segmented
                                size="small"
                                value={block.size ?? 'M'}
                                onChange={(val) =>
                                  updateBlock(block.id, {
                                    size: val as BlockSize,
                                  })
                                }
                                options={['S', 'M', 'L']}
                              />
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  ...BLOCK_TYPE_OPTIONS.map((o) => ({
                    key: o.type,
                    label: o.label,
                  })),
                  { type: 'divider' as const },
                  { key: 'qr', label: 'QR (código paquer)' },
                  { key: 'visit1', label: 'Foto de visita 1' },
                  { key: 'visit2', label: 'Foto de visita 2' },
                ],
                onClick: ({ key }) => {
                  if (key === 'qr') addQR();
                  else if (key === 'visit1')
                    addImageVar('{foto_visita_1}', 'foto-visita-1');
                  else if (key === 'visit2')
                    addImageVar('{foto_visita_2}', 'foto-visita-2');
                  else addBlock(key as BlockType);
                },
              }}
            >
              <button
                type="button"
                className={classes.addcard}
                style={{ width: '100%', padding: 9 }}
              >
                ＋ Agregar bloque ▾
              </button>
            </Dropdown>

            <div className={classes.fldLbl}>Color de marca</div>
            <div className={classes.palette}>
              {BRAND_COLORS.map((color) => (
                <span
                  key={color}
                  className={`${classes.csw}${
                    draft.brandColor === color ? ` ${classes.cswOn}` : ''
                  }`}
                  style={{ background: color }}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    setDraft((prev) => ({ ...prev, brandColor: color }))
                  }
                />
              ))}
            </div>

            <div className={classes.fldLbl}>Ancho</div>
            <Select
              value={draft.width}
              onChange={(width) => setDraft((prev) => ({ ...prev, width }))}
              options={WIDTH_OPTIONS}
              style={{ width: '100%' }}
            />
          </div>

          <div className={classes.previewPane}>
            <div
              className={classes.email}
              style={{ width: Math.min(draft.width, 360) }}
            >
              <TemplatePreview
                classes={classes}
                blocks={draft.blocks}
                brandColor={draft.brandColor}
              />
            </div>
          </div>

          <div className={classes.editorHint} style={{ gridColumn: '1/3' }}>
            Las <span className={classes.var}>{'{variables}'}</span> se
            completan con datos del envío al enviarse · vista previa con datos
            de ejemplo: {renderWithSample('{nombre_cliente}')}
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default TemplateEditorModal;
